<template>
  <div v-rect-loading="loading" class="upload-container">
    <div class="upload-editor-header">
      <div class="upload-editor-title">{{ $t(`${i18nPrefix}.title`) }}</div>
    </div>
    <div class="upload-info-container">
      <WrappedFrom
        ref="form"
        class="upload-info"
        label-width="220px"
        :model="uploadInfo"
        :formLines="formLines">
        <template #uploadFile>
          <div class="upload-file">
            <el-upload
              action=""
              :accept="accept"
              :auto-upload="false"
              :show-file-list="false"
              :on-change="handleFileChange">
              <el-button size="small" type="primary">{{ $t(`${i18nPrefix}.label.selectFile`) }}</el-button>
              <span class="file-name">{{ fileName || $t(`${i18nPrefix}.label.noSelectFile`) }}</span>
            </el-upload>
          </div>
        </template>
      </WrappedFrom>
      <a class="resume-template" :href="resumeTemplateUrl">{{ $t(`${i18nPrefix}.label.downloadResumeTemplate`) }}</a>
      <div class="bottom-btn-group">
        <el-button type="primary" :class="isEn() ? 'save-btn-en' : 'save-btn'" @click="handleSave">{{ $t(`${i18nPrefix}.label.upload`) }}</el-button>
      </div>
      <DialogWrapper
        class="upload-info-dialog"
        :show.sync="isShow"
        :loading="loading"
        :hideFooter="true"
        :title="$t(`${i18nPrefix}.label.importDataStatus`)">
        <div class="upload-result-dialog">
          <div v-if="parseFinish">
            <span>{{ $t(`${i18nPrefix}.label.successFinishImport`) }}</span>
            <b class="success-number">{{ successNumber }}</b>
            <span>{{ $t(`${i18nPrefix}.label.failedImport`) }}</span>
            <b class="failed-number">{{ failedNumber }}</b>
          </div>
          <div v-else>
            <div class="upload-loading">
              <div v-rect-loading="true" class="loading-img"></div>
              <span>{{ $t(`${i18nPrefix}.label.importing`) }}</span>
            </div>
            <div class="candidate-numb">
              <b class="progress-number">{{ progressNumber }}</b>
              <span>{{ $t(`${i18nPrefix}.label.candidate`) }}</span>
            </div>
          </div>
        </div>
      </DialogWrapper>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { isEn } from '@/i18n'
import { mapActions } from 'vuex'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'

export default {
  components: {
    WrappedFrom,
    DialogWrapper
  },
  data () {
    return {
      i18nPrefix: 'admin.company.uploadDataManagement',
      loading: false,
      isShow: false,
      isEn,
      uploadInfo: {
        jobId: null,
        uploadFile: null,
        sendingEmail: true
      },
      resumeTemplateUrl: null,
      fileName: '',
      fileType: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'application/wps-office.xlsx', 'application/wps-office.xls'],
      accept: '.xls,.xlsx',
      progressNumber: 0,
      successNumber: 0,
      failedNumber: 0,
      parseFinish: false,
      statusTimer: null
    }
  },
  computed: {
    formLines () {
      const lines = {
        jobId: {
          label: this.$t(`${this.i18nPrefix}.label.jobId`),
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        uploadFile: {
          contentSlot: 'uploadFile',
          label: this.$t(`${this.i18nPrefix}.label.selectFileWithColon`),
          rules: [{
            required: true,
            message: this.$t('common.message.requiredFormField'),
            trigger: 'change',
            validator: (rule, value, callback) => {
              if (!value) {
                callback(new Error(this.$t('common.message.requiredFormField')))
              } else {
                callback()
              }
            }
          }]
        },
        sendingEmail: {
          label: this.$t(`${this.i18nPrefix}.label.sendingNotification`),
          elType: 'el-checkbox',
          checkboxLabel: this.$t(`${this.i18nPrefix}.label.emailOrSMS`)
        }
      }

      return lines
    }
  },
  watch: {
    isShow: {
      handler (val) {
        if (!val) {
          this.resetUploadInfo()
          this.resetImportResult()
          this.clearTimer()
        }
      }
    }
  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('uploadData', ['uploadData', 'getUploadDataStatusInfo']),
    async initPage () {
      this.resumeTemplateUrl = this.$DjangoStaticUrl('/templates/resume_template.xls')
    },
    async handleSave () {
      const result = await this.$refs.form.submit()
      if (!result) {
        return
      }

      this.loading = true
      try {
        const { jobId, uploadFile, sendingEmail } = this.uploadInfo
        const params = {
          job_id: jobId,
          upload_file: uploadFile,
          sending_email: sendingEmail
        }
        const resp = await this.uploadData(params)
        if (resp) {
          this.isShow = true
          this.checkStatus()
        }
      } finally {
        this.loading = false
      }
    },
    async checkStatus () {
      if (!this.isShow) {
        return
      }

      const resp = await this.getUploadDataStatusInfo({ job_id: this.uploadInfo.jobId })
      if (!this.isShow) {
        return
      }
      if (resp) {
        this.progressNumber = resp.parse_count
        if (resp.parse_finish) {
          this.parseFinish = resp.parse_finish
          this.successNumber = resp.total_count - resp.total_failed
          this.failedNumber = resp.total_failed
        } else {
          this.clearTimer()
          this.statusTimer = _.delay(this.checkStatus, 1000)
        }
      }
    },
    clearTimer () {
      if (!this.statusTimer) {
        return
      }

      clearTimeout(this.statusTimer)
      this.statusTimer = null
    },
    resetUploadInfo () {
      this.uploadInfo = {
        jobId: null,
        uploadFile: null,
        sendingEmail: true
      }
      this.fileName = ''
    },
    resetImportResult () {
      this.progressNumber = 0
      this.successNumber = 0
      this.failedNumber = 0
      this.parseFinish = false
    },
    handleFileChange (file) {
      if (!this.fileType.includes(file.raw.type)) {
        return
      }

      this.fileName = file.name
      this.uploadInfo.uploadFile = file.raw
      // 手动触发 uploadFile 校验
      this.$nextTick(() => {
        this.$refs.form.$children[0].validateField('uploadFile')
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-container {
  .upload-editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;
    .upload-editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }
  .upload-info-container {
    width: 500px;
    margin: 30px auto 0;

    .upload-file {
      ::v-deep {
        .el-button--small {
          border-radius: 2px;
        }
      }
    }

    .file-name {
      margin-left: 5px;
    }
    .resume-template {
      position: relative;
      top: -108px;
      left: 500px;
      height: 28px;
      line-height: 28px;
      text-decoration: underline;
      cursor: pointer;
      color: #0096cc;
    }

    .bottom-btn-group {
      display: flex;
      margin-top: -25px;

      .el-button {
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;

        &:last-child {
          margin-left: 220px;
          margin-right: 0;
          background: #0096cc;
          border-color: #0096cc;
          color: #ffffff;
        }
      }
      .save-btn {
        padding: 4px 24px;
      }
      .save-btn-en {
        padding: 4px 18px;
      }
    }
  }
  .upload-info-dialog {
    .upload-result-dialog {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      height: 150px;
      .success-number {
        margin-left: 5px;
        margin-right: 10px;
      }
      .failed-number {
        margin-left: 5px;
      }
      .upload-loading {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        .loading-img {
          width: 32px;
          height: 32px;
          margin-right: 22px;
        }
      }
      .candidate-numb {
        margin: 50px 0 0 25px;
        .progress-number {
          margin-right: 5px;
        }
      }
    }
  }
}
</style>
